import {
    type MaybeRefOrGetter,
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    toValue,
    watch,
} from 'vue'

interface ScrollspyOptions {
    rootMargin?: string
    threshold?: number | number[]
}

export const useScrollspy = (
    ids: MaybeRefOrGetter<string[] | null | undefined>,
    options: ScrollspyOptions = {}
) => {
    const activeId = ref<string>('')

    const visibleElements = ref<Record<string, boolean>>({})
    const resolvedIds = computed(() => toValue(ids) || [])

    let observer: IntersectionObserver | null = null
    let isMounted = false

    function createObserver() {
        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    visibleElements.value[entry.target.id] =
                        entry.isIntersecting
                })

                const visibleIds = resolvedIds.value.filter(
                    (id) => visibleElements.value[id]
                )

                if (visibleIds.length > 0) {
                    activeId.value = visibleIds.reduce(
                        (closestId, currentId) => {
                            const closestEl = document.getElementById(closestId)
                            const currentEl = document.getElementById(currentId)

                            if (!closestEl || !currentEl) return currentId

                            return Math.abs(
                                currentEl.getBoundingClientRect().top
                            ) < Math.abs(closestEl.getBoundingClientRect().top)
                                ? currentId
                                : closestId
                        }
                    )
                }
            },
            {
                rootMargin: options.rootMargin || '-10% 0px -20% 0px',
                threshold: options.threshold || [0, 0.1, 0.5, 1.0],
            }
        )
    }

    async function observeCurrentIds() {
        await nextTick()

        resolvedIds.value.forEach((id) => {
            const el = document.getElementById(id)

            if (el) observer?.observe(el)
        })
    }

    async function resetObserver() {
        observer?.disconnect()
        visibleElements.value = {}

        createObserver()
        await observeCurrentIds()
    }

    onMounted(async () => {
        isMounted = true
        await resetObserver()
    })

    watch(resolvedIds, async () => {
        if (!isMounted) return

        await resetObserver()
    })

    onUnmounted(() => {
        isMounted = false
        observer?.disconnect()
    })

    return { activeId }
}
